import React, { useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { legalApi } from '../lib/api';

export const AuthCallback = () => {
  const hasProcessed = useRef(false);
  const { exchangeSession } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    // Prevent double processing in StrictMode
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processAuth = async () => {
      try {
        let sessionId = null;

        // Method 1: Check URL fragment (hash) - standard approach
        if (location.hash) {
          const hashParams = new URLSearchParams(location.hash.replace('#', ''));
          sessionId = hashParams.get('session_id');
        }

        // Method 2: Check URL search params - fallback for some mobile browsers
        if (!sessionId && location.search) {
          const searchParams = new URLSearchParams(location.search);
          sessionId = searchParams.get('session_id');
        }

        // Method 3: Check window.location directly - extra fallback for mobile Safari
        if (!sessionId && typeof window !== 'undefined') {
          const fullUrl = window.location.href;
          const hashMatch = fullUrl.match(/[#?&]session_id=([^&]+)/);
          if (hashMatch) {
            sessionId = decodeURIComponent(hashMatch[1]);
          }
        }

        if (!sessionId) {
          console.error('No session_id found in URL');
          navigate('/login');
          return;
        }

        // Exchange session_id for session_token
        const user = await exchangeSession(sessionId);

        // Record policy consent from Google signup if available
        const pendingConsent = sessionStorage.getItem('pendingPolicyConsent');
        if (pendingConsent && user?.user_id) {
          try {
            const policies = JSON.parse(pendingConsent);
            await legalApi.recordRegistrationConsent({ user_id: user.user_id, policies });
          } catch {}
          sessionStorage.removeItem('pendingPolicyConsent');
        }
        
        // Navigate to dashboard with user data to prevent additional auth check
        navigate('/browse', { state: { user }, replace: true });
      } catch (error) {
        console.error('Auth callback failed:', error);
        navigate('/login');
      }
    };

    // Small delay to ensure browser has fully processed the URL
    // This helps with mobile browsers that may load the hash asynchronously
    setTimeout(processAuth, 100);
  }, [exchangeSession, navigate, location]);

  return (
    <div className="min-h-screen bg-[#030014] flex items-center justify-center">
      <div className="text-center">
        <div className="spinner mx-auto mb-4" />
        <p className="text-gray-400">Signing you in...</p>
      </div>
    </div>
  );
};
